import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNotificationContext } from "contexts/NotificationContext";
import { useNavigate, useParams } from "react-router-dom";
import { RouteName } from "constants/route";
import { IUpdateFleetModelInput } from "models/fleetModel";
import updateFleetModelValidator from "validators/updateFleetModel.validator";
import { fetchFleetModelByIdApi, updateFleetModelApi } from "api/fleetModel";
import { ILoadingWithPage } from "models";
import { RcFile } from "antd/es/upload";
import { uploadFleetModelApi } from "api/upload";

export default function useEditFleetModel() {
  const id: string = useParams().id!;
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotificationContext();
  const [loading, setLoading] = useState<ILoadingWithPage>({
    button: false,
    page: true,
  });
  const { control, handleSubmit, setValue } = useForm<IUpdateFleetModelInput>({
    resolver: yupResolver(updateFleetModelValidator) as any,
  });

  useEffect(() => {
    fetchFleetModelByIdApi(id).then(({ data, error }) => {
      if (data?.data) {
        setValue("image", data.data.image ? [{ url: data.data.image }] : []);
        setValue("price_per_minute", data.data.price_per_minute.toString());
        setValue("base_fare", data.data.base_fare.toString());
        setValue("seat_number", data.data.seat_number.toString());
        setValue(
          "special_price_per_kilo_meter",
          data.data.special_price_per_kilo_meter.toString()
        );
        setValue(
          "price_per_kilo_meter",
          data.data.price_per_kilo_meter.toString()
        );
        setValue("name", data.data.name);
        setValue("special_base_fare", data.data.special_base_fare.toString());
        setValue(
          "special_price_per_minute",
          data.data.special_price_per_minute.toString()
        );
        setValue("is_shared", data.data.is_shared ?? false);
        setValue("exceptional_km", data.data.exceptional_km.toString());
        setValue(
          "exceptional_base_fare",
          data.data.exceptional_base_fare.toString()
        );
        setValue(
          "exceptional_price_per_minute",
          data.data.exceptional_price_per_minute.toString()
        );
        setValue(
          "exceptional_price_per_kilo_meter",
          data.data.exceptional_price_per_kilo_meter.toString()
        );
        setValue("manual_base_fare", (data.data.manual_base_fare ?? 0).toString());
        setValue("app_booking_percent", (data.data.app_booking_percent ?? 0).toString());
        setValue("admin_dispatch_percent", (data.data.admin_dispatch_percent ?? 0).toString());
        setValue("passenger_reward_percent", (data.data.passenger_reward_percent ?? 0).toString());
        setValue(
          "search_radius",
          data.data.search_radius !== null && data.data.search_radius !== undefined
            ? data.data.search_radius.toString()
            : ""
        );
        setLoading({ button: false, page: false });
      } else showError(error!.message);
    });
    // eslint-disable-next-line
  }, [id]);

  const onSubmit = async (input: IUpdateFleetModelInput) => {
    setLoading({ button: true, page: false });
    
    let imageUrl = null;
    if (input.image && Array.isArray(input.image) && input.image.length > 0) {
      const file = input.image[0];
      imageUrl = file.url || file.response?.data?.url || file.response?.url || null;
    }

    // Convert search_radius to number or null for API
    const payload: any = {
      ...input,
      image: imageUrl,
      search_radius: input.search_radius && input.search_radius.trim() !== ""
        ? parseFloat(input.search_radius)
        : null,
    };

    const { data, error } = await updateFleetModelApi(id, payload);
    if (data) {
      showSuccess("Fleet Model updated Successfully");
      navigate(RouteName.FLEET_MODEL, { replace: true });
    } else {
      showError(error!.message);
    }
    setLoading({ button: false, page: false });
  };

  const handleUpload = async (file: RcFile) => {
    const { data, error } = await uploadFleetModelApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };

  return { handleSubmit, onSubmit, handleUpload, control, loading };
}
